import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { querySelectorAllAsArray } from '../../../utils/query-selector-all-as-array';
import { ComboboxVariant, SelectionChangeEvent, ListItemSelection, ListBehaviour } from '../combobox.types';
import { bindChipList } from './chip-list/chip-list';
import { Keys } from '../../../shared/keys';
import { haveSameItems } from '../../../utils/have-same-items';
import { DEFAULT_LIST_BEHAVIOUR, PINNED_LIST_BEHAVIOUR, MULTI_VARIANT, SINGLE_VARIANT } from '../combobox.constants';

/**
 * @slot - wb-combobox-list-items has to be slotted here (required)
 */
@Component({
  tag: 'wb-combobox-list',
  styleUrl: './combobox-list.scss',
  shadow: true,
})
export class ComboboxListComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   * shows status of overlay
   * if true, shows overlay
   */
  @Prop({ reflect: true, mutable: true })
  public open = false;

  /** controls the appearance of the elements in the list */
  @Prop({ reflect: true })
  public variant: ComboboxVariant = SINGLE_VARIANT;

  /**
   * Controls if it should scroll to selected item.
   * Only usable for single select variant.
   * @default true
   */
  @Prop({ reflect: true })
  public autoscroll: boolean = true;

  /**
   * Conditionally renders row with the selected values as removable chips.
   * @default true
   */
  @Prop({ reflect: true })
  public showChipListRow?: boolean = true;

  /**
   * Text to show that no elements are selected.
   * Only relevant if showChipListRow is true.
   */
  @Prop({ reflect: true })
  public noItemsSelectedText?: string = 'No values selected';

  /**
   * Text to show when no items match the given search value.
   */
  @Prop({ reflect: true })
  public noItemsFoundText: string = 'No values found';

  /**
   * Search value of the combobox input
   * @internal
   */
  @Prop({ mutable: true })
  public searchValue: string = '';

  /** The position of the list. Values allowed:
   * - 'overlay': default, the list will overlap only the content
   * - 'inline': the list will take the available space
   * - 'pinned': the list will overlap everything on the page
   */
  @Prop({ reflect: true, mutable: true })
  public listBehaviour?: ListBehaviour = DEFAULT_LIST_BEHAVIOUR;

  /**
   * Emitted when selection changes.
   */
  @Event()
  public wbcomboboxlistselectionchange: EventEmitter<SelectionChangeEvent>;

  /** Emitted when the overlay closes */
  @Event()
  public wbcomboboxlistclose: EventEmitter<{ clearInput?: boolean }>;

  private inputEl: HTMLWbComboboxInputElement;

  private TagNames = transformTagNames(this, 'wb-combobox-list', ['wb-combobox-input', 'wb-combobox-list-item', 'wb-list-group', 'wb-list-group-item']);

  @State() private overlayWidth: string = '';
  @State() private overlayHeight: string = '';
  @State() private overlayLeft: string = '';
  @State() private overlayTop: string = '';

  @State() private isMultiSelect = false;

  @State() private listItemElements: Array<HTMLWbComboboxListItemElement> = [];

  @State() private overlayEl: HTMLElement;

  @State() private isHandlingSelection = false;

  /**
   * Returns the selected list items.
   */
  @Method()
  public async getSelectedComboboxListItemEls(): Promise<ListItemSelection> {
    this.setListItemElements();
    const selectedItems = this.getSelectedListItems();
    const selection = this.getSelection(selectedItems);
    return this.isMultiSelect ? selection : selection[0];
  }

  /**
   * Resets all the selected values.
   */
  @Method()
  public async resetSelection(): Promise<void> {
    this.listItemElements = this.listItemElements.map((item: HTMLWbComboboxListItemElement) => {
      item.selected = false;
      return item;
    });
  }

  connectedCallback() {
    this.isMultiSelect = this.variant === MULTI_VARIANT;

    if (this.el.previousElementSibling.tagName.toLowerCase() === this.TagNames.WbComboboxInput.toLowerCase()) {
      this.inputEl = this.el.previousElementSibling as HTMLWbComboboxInputElement;
    } else {
      console.error('A wb-combobox-input has to be provided next to the combobox-list!');
    }

    this.setListItemElements();

    if (this.open) {
      // Filter data by
      this.filterSelectValues();
    }
  }
  public componentWillLoad(): void {
    if (this.listBehaviour === PINNED_LIST_BEHAVIOUR) {
      this.setPosition();
      this.checkInputVisibility();
      window.addEventListener('resize', this.setPosition);
      window.addEventListener('scroll', this.setPosition);
    }
  }

  @Listen('keydown', { target: 'document' })
  public onKeyDown(e: KeyboardEvent): void {
    if (this.open && e.key === Keys.ESC) {
      this.closeOverlay();
    }
  }

  @Watch('variant')
  public onVariantChange(newValue: ComboboxVariant, oldValue: ComboboxVariant): void {
    const variantChanged = newValue !== oldValue;

    if (variantChanged) {
      this.isMultiSelect = newValue === MULTI_VARIANT;
    }
  }

  @Watch('open')
  public onOpenChange(newValue: boolean, oldValue: boolean): void {
    const wasJustOpened = newValue !== oldValue;

    if (wasJustOpened) {
      // needs to run as separate task in event queue
      setTimeout(() => {
        document.addEventListener('click', this.closeOverlayWhenClickedOutside);
      });

      this.setListItemElements();

      if (this.listBehaviour === PINNED_LIST_BEHAVIOUR) {
        this.setPosition();
      }

      if (!this.isMultiSelect && this.autoscroll) {
        // Single select varaiant given, so you can be sure it only contains one selected list item
        const selectedItem = this.getSelectedListItems()?.[0];

        if (!selectedItem) {
          return;
        }

        // The list group el is the scrollable element
        const listGroupEl = this.overlayEl.querySelector('.selection-items-wrapper');

        window.requestAnimationFrame(() => {
          const overlayBoundaries = this.overlayEl.getBoundingClientRect();
          const selectedItemBoundaries = selectedItem.getBoundingClientRect();
          // Calcuate offset by subtracting the offset of the selected item with it's height
          // so you can be sure the element is not outside of the overlay boundary.
          // By removing the half of the overlay boundary height you only scroll to
          // the center of the overlay height and not to the bottom of it.
          const scrollTop = selectedItem.offsetTop - selectedItemBoundaries.height - overlayBoundaries.height / 2;

          // If selected item is in the visible area you don't have to scroll
          if (selectedItem.offsetTop > overlayBoundaries.height) {
            listGroupEl.scrollTo({ top: scrollTop });
          }
        });
      }
    }
  }

  @Watch('searchValue')
  public onSearchValueChange(newValue: string, oldValue: string): void {
    const valueChanged = newValue !== oldValue;

    if (!valueChanged) {
      return;
    }

    if (this.open) {
      this.filterSelectValues();
    } else {
      // If search value is changed and overlay is closed, all items should be visible again
      this.listItemElements = this.listItemElements.map((item) => {
        item.hidden = false;
        return item;
      });
    }
  }

  @Watch('listItemElements')
  public onListItemsArrChange(newValue: Array<HTMLWbComboboxListItemElement>, prevValue: Array<HTMLWbComboboxListItemElement>): void {
    const listItemsUnchanged = haveSameItems(newValue, prevValue);

    if (listItemsUnchanged) {
      return;
    }

    this.listItemElements = this.listItemElements.map((item: HTMLWbComboboxListItemElement) => {
      item.addEventListener('wbcomboboxlistitemselectionchange', this.handleSelection);

      return item;
    });
  }

  @Watch('listBehaviour')
  public onListBehaviourChange(): void {
    this.setPosition();
  }
  private setPosition = (): void => {
    if (this.listBehaviour !== PINNED_LIST_BEHAVIOUR) {
      this.cleanPositionStyles();
      return;
    }

    const inputArea = this.inputEl.getBoundingClientRect();
    if (!inputArea) {
      return;
    }

    const viewportHeight = window.innerHeight;
    const availableSpace = viewportHeight - inputArea.bottom;

    this.overlayWidth = `${inputArea.width}px`;
    this.overlayHeight = availableSpace > 0 ? `${availableSpace}px` : '0px';
    this.overlayLeft = this.open ? `${inputArea.left}px` : '0px';
    this.overlayTop = this.open ? `${inputArea.bottom}px` : '0px';
  };

  private cleanPositionStyles = (): void => {
    this.overlayWidth = '';
    this.overlayHeight = '';
    this.overlayLeft = '';
    this.overlayTop = '';
  };

  private checkInputVisibility = (): void => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            this.closeOverlay();
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    observer.observe(this.inputEl);
  };

  private handleSelection = (event: CustomEvent<{ value: any }>): void => {
    if (this.isHandlingSelection) {
      return;
    }

    this.isHandlingSelection = true;

    const index = this.listItemElements.findIndex((listItem: HTMLWbComboboxListItemElement) => listItem.isEqualNode(event.target as HTMLWbComboboxListItemElement));

    const selectedValue = event.detail.value;

    if (this.isMultiSelect) {
      this.handleMultiSelection(selectedValue, index);
    } else {
      this.handleSingleSelection(selectedValue, index);
    }

    this.isHandlingSelection = false;

    this.emitUpdatedSelection();
  };

  private handleMultiSelection = (selectedValue: boolean, selectedItemIndex: number): void => {
    const listItemElementsCopy = [...this.listItemElements];

    listItemElementsCopy[selectedItemIndex].selected = selectedValue;

    this.listItemElements = listItemElementsCopy;
  };

  private handleSingleSelection = (newSelectedValue: boolean, selectedItemIndex: number): void => {
    this.listItemElements = this.listItemElements.map((item: HTMLWbComboboxListItemElement, index: number) => {
      if (index === selectedItemIndex) {
        item.selected = newSelectedValue;
      } else if (newSelectedValue === true && item.selected === true) {
        item.selected = false; // Deselect only if newSelectedValue is true
      }
      item.hidden = false; // After selection set, all items should be visible again

      return item;
    });

    this.closeOverlay(true);
  };

  private emitUpdatedSelection(): void {
    const selectedItems = this.getSelectedListItems();
    const selection = this.getSelection(selectedItems);

    this.wbcomboboxlistselectionchange.emit({ source: this.el, value: this.isMultiSelect ? selection : selection[0] });
  }

  private getSelection(selectedItems: Array<HTMLWbComboboxListItemElement>) {
    return selectedItems.map(({ value, label }) => ({ value, label }));
  }

  private setListItemElements(): void {
    this.listItemElements = querySelectorAllAsArray(this.el, this.TagNames.WbComboboxListItem);
  }

  private setOverlayRef = (el: HTMLElement): void => {
    this.overlayEl = el;

    this.overlayEl.addEventListener('wbchange', (event: CustomEvent) => {
      event.stopPropagation(); // stop wbchange event from wb-checkbox elements from bubbling up
    });
  };

  private filterSelectValues = (): void => {
    if (this.searchValue.length) {
      this.listItemElements = this.listItemElements?.map((item: HTMLWbComboboxListItemElement) => {
        item.hidden = !item.innerText?.toLowerCase().includes(this.searchValue.toLowerCase());

        return item;
      });
    } else {
      // If no search value present return all list elements
      this.listItemElements = this.listItemElements?.map((item: HTMLWbComboboxListItemElement) => {
        item.hidden = false;

        return item;
      });
    }
  };

  private closeOverlayWhenClickedOutside = (event: MouseEvent): void => {
    const isClickInsideOverlay = event.composedPath().includes(this.el);
    const isClickInsideInput = event.composedPath().includes(this.inputEl);

    if (!isClickInsideOverlay && !isClickInsideInput) {
      this.closeOverlay();
    }
  };

  private getSelectedListItems = (): Array<HTMLWbComboboxListItemElement> => this.listItemElements.filter((listItem: HTMLWbComboboxListItemElement) => listItem.selected);

  private closeOverlay(clearInput?: boolean): void {
    this.open = false;

    // remove any no longer needed click-outside-listeners
    document.removeEventListener('click', this.closeOverlayWhenClickedOutside);

    // notify all subscriber that the overlay has been closed
    this.wbcomboboxlistclose.emit({ clearInput });
  }

  private removeItemFromChipList = (itemToRemove: HTMLWbComboboxListItemElement): void => {
    const indexItemToRemove = this.listItemElements.findIndex((listItem: HTMLWbComboboxListItemElement) => listItem.isEqualNode(itemToRemove));

    const newListItemElements = [...this.listItemElements];

    newListItemElements[indexItemToRemove].selected = false;

    this.listItemElements = newListItemElements;
  };

  private handleListElementsSlotChange = (): void => {
    const oldListItemElements = this.listItemElements;
    this.setListItemElements();
    if (!haveSameItems(this.listItemElements, oldListItemElements)) {
      setTimeout(() => this.emitUpdatedSelection(), 0);
    }
  };

  public render(): JSX.Element {
    const ChipList = bindChipList(this);

    const { WbListGroup, WbListGroupItem } = this.TagNames;
    const searchResultsFound = this.listItemElements.filter((item: HTMLWbComboboxListItemElement) => !item.hidden).length;
    const selectedListItems = this.getSelectedListItems();

    return (
      <Host class="wb-combobox-list">
        <div
          data-testid="wb-combobox-overlay"
          class="overlay"
          tabindex="-1" //
          inert={!this.open}
          ref={this.setOverlayRef}
          style={{
            width: this.overlayWidth,
            height: this.overlayHeight,
            left: this.overlayLeft,
            top: this.overlayTop,
          }}
        >
          {this.isMultiSelect && this.showChipListRow && searchResultsFound ? ( //
            <ChipList selectedListItems={selectedListItems} noItemsSelectedText={this.noItemsSelectedText} removeItemFromChipList={this.removeItemFromChipList} />
          ) : null}

          <WbListGroup class="selection-items-wrapper">{searchResultsFound ? <slot onSlotchange={this.handleListElementsSlotChange} /> : <WbListGroupItem class="no-results">{this.noItemsFoundText}</WbListGroupItem>}</WbListGroup>
        </div>
      </Host>
    );
  }
}
